/* eslint-disable brace-style */
import { ActivatedRoute } from '@angular/router';
import { FirebaseError } from '@angular/fire/app/firebase';

import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';

import { catchError, filter, firstValueFrom, map, Observable, of, pairwise, shareReplay, skipUntil, startWith, Subject, takeUntil, tap, timer } from 'rxjs';

import { Banner, Message } from '@app/shared/models/app.model';
import { AccountService } from '@app/services/account.service';
import { BackendTranslatePipe } from '@app/pipes/backend-translate.pipe';
import { ImproveAudioDialogComponent } from '@app/components/improve-audio-dialog/improve-audio-dialog.component';
import { LanguageService } from '@app/services/language.service';
import { License } from '@app/modules/license/license.types';
import { MatDialog } from '@angular/material/dialog';
import { Microphone } from '@app/shared/models/microphone.model';
import { NoSleepService } from '@app/services/no-sleep.service';
import { SpeechService } from '@app/services/speech.service';
import { StableTextService } from '@app/directives/stable-text/stable-text.service';

import {
  IRemoteSettings,
  SettingsService,
} from '@app/services/settings.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { GuestSessionService } from '@app/services/guest-session.service';
import { licenseDeviceUuid } from '@app/services/transcript/license-device-uuid';
import { OwnerTranscriptConnectionManager } from '@app/services/transcript/owner-transcript-connection.manager';
import { SessionService } from '@app/services/session.service';
import { SpeakseeOrderService } from '@app/modules/license/license.service';
import { TranslocoService } from '@jsverse/transloco';

@HostListener('window:scroll', [])
@Component({
  selector: 'app-speech',
  templateUrl: './speech.component.html',
  styleUrls: ['./speech.component.scss'],
  providers: [StableTextService],
  // changeDetection: ChangeDetectionStrategy.Default,
})
export class SpeechComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('scrollView') scrollView: ElementRef<HTMLDivElement>;

  public userActing = false;
  // TODO: Use messages Observable for getting new message data
  // this.messages.subscribe()
  // cancel subscription in ngDestroy
  // i can update a public variable for the notifications and send that to the snackbar
  public messages$: Observable<Message[]>;
  public microphones$: Observable<Microphone[]>;
  public messageLength: number;
  public kitLicenses: License[];
  public allLicenses: License[];

  public poorAudioBanner = signal<Banner | null>(null);
  public isAudioBannerDismissed = signal(false);
  public translationsDisabledBanner = signal<Banner | null>(null);
  public translationsEnabledBanner = signal<Banner | null>(null);

  private autoScrollEnabled = true;
  private scrollTimeout: any;
  private stopTranscription = new Subject();

  private stableTextService = inject(StableTextService);
  private ownerTranscripts = inject(OwnerTranscriptConnectionManager);
  private backendTranslatePipe = new BackendTranslatePipe();

  private _rawMessages = signal<Message[]>([]);

  displayedMessages = computed(() => {
    if (this.isQRAuthMode && this.qrUserService.isSoundshapeQR() && this.qrUserService.effectiveBufferMs() > 0) {
      return this.stableTextService.displayReadyMessages();
    }
    return this._rawMessages();
  });

  protected session: License;
  protected isDarkMode$ = this.settingsService.darkMode$;
  protected isQRAuthMode = this.qrUserService.isGuestUser();

   private readonly REQUIRED_FINAL_MESSAGES_COUNT = 5;
   private readonly LOW_CONFIDENCE_THRESHOLD = 0.4;
   private readonly HIGH_CONFIDENCE_MESSAGES_COUNT = 5;

  constructor(
    route: ActivatedRoute,
    private accountService: AccountService,
    private languageService: LanguageService,
    private speechService: SpeechService,
    private noSleepService: NoSleepService,
    private settingsService: SettingsService,
    private sessionService: SessionService,
    private orderService: SpeakseeOrderService,
    private _dialogService: MatDialog,
    private qrUserService: GuestSessionService,
    private translocoService: TranslocoService
  ) {
    this.session = route.snapshot?.data.session;
    this.allLicenses = this.orderService.licenses;
    this.kitLicenses = this.allLicenses.filter(
      (license) => license.type === 'microphonekit'
    );

    this.getPoorAudioBanner$()
      .pipe(
        takeUntilDestroyed(),
        filter(banner => banner !== this.poorAudioBanner()))
      .subscribe((newBanner) => this.poorAudioBanner.set(newBanner))

    if (this.isQRAuthMode && this.qrUserService.isSoundshapeQR()) {
      const prevBufferMs = signal(0);
      effect(() => {
        const ms = this.qrUserService.effectiveBufferMs();
        const prev = untracked(() => prevBufferMs());
        this.stableTextService.setTranslationBufferMs(ms);

        // Only commit existing messages when delay first activates (0 → >0),
        // not on every buffer adjustment (e.g. 3000 → 5000)
        if (ms > 0 && prev === 0) {
          const rawMessages = untracked(() => this._rawMessages());
          if (rawMessages.length > 0) {
            const preTranslated = this.preTranslateForQR(rawMessages);
            for (const msg of preTranslated) {
              this.stableTextService.commitInstantly(msg.documentID, msg.message, msg.is_final);
            }
            this.stableTextService.setMessages(preTranslated);
          }
        }
        prevBufferMs.set(ms);
      }, { allowSignalWrites: true });
    }

    // Only show banners when state changes WHILE user is viewing (not on initial load)
    if (this.isQRAuthMode) {
      toObservable(this.qrUserService.guestTranslationsEnabled)
        .pipe(
          takeUntilDestroyed(),
          startWith(null as boolean | null),
          pairwise()
        )
        .subscribe(([previous, current]) => {
          // Show disabled banner when transitioning from enabled (true) to disabled (false)
          if (previous === true && current === false) {
            this.translationsEnabledBanner.set(null);
            this.translationsDisabledBanner.set(this.createTranslationsBanner(false));
          }
          // Show enabled banner when transitioning from disabled (false) to enabled (true)
          else if (previous === false && current === true) {
            this.translationsDisabledBanner.set(null);
            this.translationsEnabledBanner.set(this.createTranslationsBanner(true));
          }
        });
    }
  }

  private createTranslationsBanner(enabled: boolean): Banner {
    const translationKey = enabled
      ? 'qr_translations.enabled_banner_message'
      : 'qr_translations.disabled_banner_message';
    const message = this.translocoService.translate(translationKey);

    return {
      documentID: enabled ? 'qr-translations-enabled' : 'qr-translations-disabled',
      content: { en: message, nl: message, de: message, fr: message },
      backgroundColor: enabled ? '#4caf50' : '#ff9800',
      persistent: !enabled,
    };
  }

  /**
   * Listen on all events that could perhaps trigger a user scroll
   * If so we inform our scrolling system to not trigger auto scroll until
   * interaction is over.
   */
  // NOTE: consider mouse grabbing scroll wheel or up/down arrow
  @HostListener('window:wheel', ['$event'])
  @HostListener('window:mousedown', ['$event'])
  @HostListener('window:mouseup', ['$event'])
  @HostListener('window:touchstart', ['$event'])
  @HostListener('window:touchcancel', ['$event'])
  @HostListener('window:touchend', ['$event'])
  mouse($event) {
    switch ($event.type) {
      case 'wheel':
        this.userActing = !this.isAtBottom() || $event.deltaY < 0;
        break;
      case 'touchstart':
      case 'mousedown':
        this.userActing = true;
        break;
      case 'touchend':
      case 'touchcancel':
      case 'mouseup':
        this.userActing = false;
        break;
    }
  }

  /**
   * Checks if the scroll view is at the bottom.
   *
   * @returns True if the scroll view is at the bottom, false otherwise.
   */
  isAtBottom() {
    const target = this.scrollView?.nativeElement;
    if (!target) {
      return true;
    }

    const current = target.offsetHeight + target.scrollTop;
    // const offset = 100;
    return current >= target.scrollHeight - 100;
  }

  /**
   * Handles the window scroll event and sets the autoScrollEnabled to true if
   * the scroll view is at the bottom.
   */
  onWindowScroll() {
    if (this.isAtBottom()) {
      clearTimeout(this.scrollTimeout);
      this.autoScrollEnabled = true;
    }

    if (this.userActing) {
      this.autoScrollEnabled = false;
    }
  }

  /**
   * Scrolls the view to the bottom.
   */
  scrollToBottom() {
    this.scrollTimeout = setTimeout(() => {
      this.scrollView.nativeElement.scrollTo({
        top: this.scrollView.nativeElement.scrollHeight,
        behavior: 'smooth',
      });
    });
  }

  ngOnInit(): void {
    // IDEA: Move this into a resolver?
    try {
      this.noSleepService.enable();
    } catch (e) {
      console.warn('Failed to enable wake lock', e);
    }

    this.microphones$ = this.speechService.activeMics$;

    this.settingsService.kit$
      .pipe(takeUntil(this.stopTranscription))
      .subscribe((settings: IRemoteSettings) => {
        for (const mic of settings.microphones) {
          this.speechService.updateMic(mic.mic_id, {
            color: mic.color_hex,
            name: mic.name,
          });
        }
      });
  }

  async ngAfterViewInit() {
    let languageSetFromInitLoad = false;
    const licensesToListenTo = [this.session];
    const acLicense = this.allLicenses.find((l) => l.type === 'autocaption');
    if (this.session.type === 'microphonekit' && acLicense) {
      licensesToListenTo.push(acLicense);
      await this.sessionService.startSession(acLicense).toPromise();
    }

    //this.settingsService.startMicSettingsPoller(this.stopTranscription);

    // Owners (logged-in, non-guest) have no transcript websocket otherwise;
    // open one client_type=app connection per watched device. Guests reuse the
    // already-open guest websocket (QRWebsocketService).
    if (!this.qrUserService.isGuestUser()) {
      const ownerJwt = this.accountService.userValue?.token?.accessToken;
      if (ownerJwt) {
        for (const license of licensesToListenTo) {
          const deviceUuid = licenseDeviceUuid(license);
          if (deviceUuid) {
            this.ownerTranscripts.connect(deviceUuid, ownerJwt);
          }
        }
      } else {
        console.warn(
          '[OwnerTranscripts] no access token — skipping owner transcript websocket connections'
        );
      }
    }

    // Initialize guest websocket: QR-soundshape users and shared-session guests
    if (this.qrUserService.isGuestUser() && this.qrUserService.isSoundshapeQR()) {
      await firstValueFrom(this.qrUserService.initializeWebsocket());
    }

    this.speechService
      // Owners read transcripts from Firestore (every device writes there).
      // Guests (soundshape QR / shared sessions) have no Firestore access and
      // read from the WebSocket stream instead.
      .startListener(licensesToListenTo, undefined, !this.qrUserService.isGuestUser())
      .pipe(
        takeUntil(this.stopTranscription),

        // TODO: Move this into the speech.service
        // Catch firebase no permission error and redirect user to selector
        catchError((err: any) => {
          console.error(err);
          if ((err as FirebaseError).code === 'permission-denied') {
            this.accountService.logout();
          }
          this.stopTranscription.next(true);
          return of([]);
        }),
        // Soundshape QR users only: check transcript on each message and retry if needed
        // (Microphonekit QR uses frontend translation, so no transcript retry needed)
        tap((messages: Message[]) => {
          if (this.isQRAuthMode && messages.length > 0 && this.qrUserService.isSoundshapeQR()) {
            const latestMessage = messages[messages.length - 1];
            const targetLang = this.settingsService.getLocalLanguageOut();
            if (latestMessage.transcripts && targetLang) {
              this.qrUserService.checkAndRetryTranscript(latestMessage.transcripts, targetLang);
            }
          }
        }),
        map((messages: Message[]) => {
          if (!messages.length) {
            return messages;
          }

          if (!languageSetFromInitLoad) {
            const langCode = messages[messages.length - 1].language || 'en-US';
            this.languageService.setTranscriptionLanguage(langCode);
            languageSetFromInitLoad = !languageSetFromInitLoad;
          }

          if (this.autoScrollEnabled) {
            clearTimeout(this.scrollTimeout);
            this.scrollToBottom();
          }
          return messages;
        }),
        shareReplay(1)
      )
      .subscribe(
        (messages: Message[]) => {
          if (this.isQRAuthMode && this.qrUserService.isSoundshapeQR() && this.qrUserService.effectiveBufferMs() > 0) {
            const preTranslated = this.preTranslateForQR(messages);
            this.stableTextService.setMessages(preTranslated);
          }

          this._rawMessages.set(messages);
          this.messages$ = of(messages);
          this.messageLength = messages.length;
        },
        (err: any) => {
          console.error(err);
        }
      );
  }

  private createLowConfidenceBanner(): Banner {
    return {
      documentID: 'low-confidence',
      content: {
        en: 'Low audio quality detected. Some parts of the conversation may not be transcribed correctly. Tap here for improvement tips.',
        nl: 'Lage audiokwaliteit gedetecteerd. Sommige delen van het gesprek worden mogelijk niet correct getranscribeerd. Tik hier voor verbeteringstips.',
      },
      backgroundColor: '#ff9966',
      persistent: true,
    };
  }

  private calculateAverageConfidence(messages: Message[]): number {
    if (!messages || messages.length === 0) {
      return 1.0;
    }
    const totalConfidence = messages.reduce(
      (sum, msg) => sum + msg.confidence,
      0
    );
    return totalConfidence / messages.length;
  }


  public getPoorAudioBanner$(): Observable<Banner | null> {
    return this.speechService.messages$.pipe(
      takeUntil(this.stopTranscription),
      skipUntil(timer(3 * 60 * 1000)),  // Skip emissions for the first 3 minutes
      filter((messages) => !this.isAudioBannerDismissed() && messages.length > 0),
      map((messages) => {
        const finalMessages = messages.filter((msg) => msg.is_final);
        const currentBanner = this.poorAudioBanner();
        let newBannerState: Banner | null = currentBanner; 

        // Condition 1: Not enough final messages processed yet to make a reliable decision.
        // Ensure no banner is shown during this initial phase.
        if (finalMessages.length < this.REQUIRED_FINAL_MESSAGES_COUNT) {
          newBannerState = null;
        }
        // Condition 2: Enough messages, no banner currently shown. Check if confidence is low enough to show it.
        else if (currentBanner === null) {
          const messagesForShowingCheck = finalMessages.slice(-this.REQUIRED_FINAL_MESSAGES_COUNT);
          const avgConfidence = this.calculateAverageConfidence(messagesForShowingCheck);

          if (avgConfidence < this.LOW_CONFIDENCE_THRESHOLD) {
            newBannerState = this.createLowConfidenceBanner();
          }
        }
        // Condition 3: Enough messages, a banner is currently shown. Check if confidence is high enough to hide it.
        // Note: This check might use a different message count (HIGH_CONFIDENCE_MESSAGES_COUNT).
        else if (finalMessages.length >= this.HIGH_CONFIDENCE_MESSAGES_COUNT && newBannerState) {
          const messagesForHidingCheck = finalMessages.slice(-this.HIGH_CONFIDENCE_MESSAGES_COUNT);
          const avgConfidence = this.calculateAverageConfidence(messagesForHidingCheck);

          if (avgConfidence >= this.LOW_CONFIDENCE_THRESHOLD) {
            newBannerState = null;
          }
        }
        // If none of the above conditions are met, newBannerState remains unchanged (equal to currentBanner).
        return newBannerState;
      })
    );
  }

  public openImproveAudioDialog() {
    this._dialogService.open(ImproveAudioDialogComponent, { autoFocus: false });
  }

  public dismissBanner() {
    const dismissBannerExpiration = 1000 * 60 * 45;

    this.isAudioBannerDismissed.set(true);
    this.poorAudioBanner.set(null);
    setTimeout(() => {
      this.isAudioBannerDismissed.set(false);
    }, dismissBannerExpiration)
  }

  /**
   * Pre-translate messages for Soundshape QR users so that StableTextService
   * types the translated text word-by-word (not the raw recognition text).
   */
  private preTranslateForQR(messages: Message[]): Message[] {
    const targetLang = this.settingsService.getLocalLanguageOut();
    const translationsDisabled = this.qrUserService.guestTranslationsEnabled() === false;

    return messages.map(msg => {
      const translated = this.backendTranslatePipe.transform(
        msg.message, msg.transcripts, msg.language, targetLang, translationsDisabled
      );
      if (translated === msg.message) return msg;
      return { ...msg, message: translated };
    });
  }

  ngOnDestroy() {
    this.stopTranscription.next(true);
    this.ownerTranscripts.disconnectAll();
    try {
      this.noSleepService.disable();
    } catch (e) {
      console.warn('failed to disable wake lock', e);
    }
  }
}
